import { FormGroup } from '@components/molecules/form/FormGroup';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import ReactSelect from '@components/atoms/ReactSelect';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Employee } from '@models/employee.model';
import { useCreateEmployee } from '@hooks/api/employees/useCreateEmployee';
import { useEditEmployee } from '@hooks/api/employees/useEditEmployee';
import { useProjects } from '@hooks/api';
import {
  MODE_OPTIONS,
  PAY_PERIOD_OPTIONS,
  USER_TYPE_OPTIONS,
  getRoleTypeOptions,
} from '@pages/users/constants/constants';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { canChangeUserRole } from '@constants/roleHierarchy';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { createEditValidationSchema } from '@pages/users/constants/createEditValidationSchema';
import {
  isNoSeatsAvailableError,
  getNoSeatsExtensions,
} from '@utils/errorMessage';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  superintendentId: string | null;
  dob: string;
  hireDate: string;
  rate: number | null;
  payPeriod: string;
  role: string;
  mode: string;
  managerProjectIds: string[];
  superintendentProjectIds: string[];
  reviewerProjectIds: string[];
};

// Sprint 23, story-111 (m3sgj7qD). Module-level stable reference — useProjects()'s own `= []`
// default used to be a new array identity every render while the query had no data, which fed a
// new-array -> new-callback -> effect -> setState -> render loop (354 React errors on a single
// /users load). A stable default plus useMemo (below) removes the loop at the source rather than
// working around it.
const NO_PROJECTS: any[] = [];

interface CreateEditEmployeeModalProps {
  role: string | null;
  title: string;
  showNewModal: boolean;
  initialData?: Employee | null;
  toggleNewModal: () => void;
  onUserCreated?: (role: string) => void;
  // Sprint 20, story-093: fired when the create/edit mutation is refused server-side with
  // NO_SEATS_AVAILABLE (the pre-check was stale or bypassed). The form stays open with its
  // typed values intact — the caller opens the existing out-of-seats modal on top.
  onSeatsExhausted?: (info: { occupiedSeats: number; includedSeats: number } | null) => void;
}

export const CreateEditEmployeeModal = ({
  role,
  title,
  showNewModal,
  initialData,
  toggleNewModal,
  onUserCreated,
  onSeatsExhausted,
}: CreateEditEmployeeModalProps) => {
  const createEmployeeMutation = useCreateEmployee();
  const editEmployeeMutation = useEditEmployee();
  const [avatarFiles, setAvatarFiles] = useState<File[]>([]);

  const { role: actorRole } = useAppSelector((state: RootState) => state.user);
  // Only the roles this user may assign (their own level and below).
  const roleOptions = useMemo(() => getRoleTypeOptions(actorRole), [actorRole]);

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      superintendentId: null,
      dob: '',
      hireDate: '',
      rate: null,
      payPeriod: '',
      role: role || '',
      mode: '',
      managerProjectIds: [],
      superintendentProjectIds: [],
      reviewerProjectIds: [],
    },
    validationSchema: createEditValidationSchema,
    onSubmit: async (values) => {
      const avatarFile = avatarFiles.length > 0 ? avatarFiles[0] : null;
      try {
        if (initialData) {
          await editEmployeeMutation.mutateAsync({
            userId: initialData.user?.userId as string,
            employeeId: initialData.employeeId,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            payPeriod: values.payPeriod,
            rate: values.rate,
            dob: values.dob,
            hireDate: values.hireDate,
            mode: values.mode,
            role: values.role || undefined,
            managerProjectIds: values.managerProjectIds,
            superintendentProjectIds: values.superintendentProjectIds,
            reviewerProjectIds: values.reviewerProjectIds,
            file: avatarFile,
          });
        } else {
          const result = await createEmployeeMutation.mutateAsync({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            payPeriod: values.payPeriod,
            rate: values.rate,
            dob: values.dob,
            hireDate: values.hireDate,
            mode: values.mode,
            role: values.role || undefined,
            managerProjectIds: values.managerProjectIds,
            superintendentProjectIds: values.superintendentProjectIds,
            reviewerProjectIds: values.reviewerProjectIds,
            file: avatarFile,
          });

          // Navigate to the newly created user's role tab
          if (result?.role && onUserCreated) {
            onUserCreated(result.role);
          }
        }
        toggleNewModal();
        formik.resetForm();
        setAvatarFiles([]);
      } catch (error) {
        // Sprint 20, story-093: a stale-or-bypassed pre-check refusal. Stay open, keep the
        // typed values, and hand off to the existing out-of-seats modal instead of the
        // generic error toast (already suppressed in the mutation hooks themselves for this
        // code). Any other error re-throws so it surfaces exactly as it does today.
        if (isNoSeatsAvailableError(error)) {
          onSeatsExhausted?.(getNoSeatsExtensions(error));
          return;
        }
        throw error;
      }
    },
  });

  const { setValues, resetForm, setFieldValue } = formik;

  useEffect(() => {
    if (initialData) {
      setValues({
        firstName: initialData.user?.firstName || '',
        lastName: initialData.user?.lastName || '',
        email: initialData.user?.email || '',
        superintendentId: null,
        payPeriod: initialData.payPeriod || '',
        rate: initialData.rate || null,
        dob: (initialData.dob as string) || '',
        hireDate: (initialData.hireDate as string) || '',
        role: initialData.user?.role || '',
        mode: initialData.mode || '',
        managerProjectIds: (initialData.managerProjects || [])
          .map((p) => p.projectId)
          .filter(Boolean) as string[],
        superintendentProjectIds: (initialData.superintendentProjects || [])
          .map((p) => p.projectId)
          .filter(Boolean) as string[],
        reviewerProjectIds: (initialData.reviewerProjects || [])
          .map((p) => p.projectId)
          .filter(Boolean) as string[],
      });
      setAvatarFiles([]);
    } else {
      resetForm();
      setAvatarFiles([]);
    }
  }, [initialData, setValues, resetForm]);


  // Ensure role defaults from prop when opening the modal for a new user. Falls
  // back to the first assignable role when there is no active tab (search mode),
  // so a new user is never created with an empty role.
  useEffect(() => {
    if (showNewModal && !initialData) {
      const preselected =
        role && roleOptions.some((o) => o.value === role) ? role : '';
      setFieldValue('role', preselected || roleOptions[0]?.value || '');
    }
  }, [showNewModal, role, initialData, setFieldValue, roleOptions]);

  // Role-based visibility logic
  const selectedRole =
    formik.values.role || initialData?.user?.role || role || '';

  // Lock the role when editing someone this user has no authority to re-role:
  // a peer (or themselves), or a role outside their assignable set. Their other
  // profile fields stay editable.
  const targetCurrentRole = initialData?.user?.role;
  const roleSelectLocked =
    !!initialData &&
    (!canChangeUserRole(actorRole, targetCurrentRole) ||
      !roleOptions.some((o) => o.value === targetCurrentRole));
  const isEmployee = selectedRole === 'EMPLOYEE';
  const isCrewLead = selectedRole === 'CREW_LEAD';
  const isProjectManager = selectedRole === 'PROJECT_MANAGER';
  const isSuperintendent = selectedRole === 'SUPERINTENDENT';
  const isReviewer = selectedRole === 'REVIEWER';

  const isLoading =
    createEmployeeMutation.isPending || editEmployeeMutation.isPending;

  // Projects for PM and Superintendent
  const { data: projects = NO_PROJECTS, refetch } = useProjects();

  useEffect(() => {
    if (showNewModal) {
      refetch();
    }
  }, [showNewModal, refetch]);

  const projectOptions = useMemo(() => {
    return (projects || [])
      .filter((p: any) => {
        if (!p.projectId) return false;
        if (isProjectManager) {
          const assigned =
            !!p.projectManagerId ||
            !!p.projectManager?.employeeId ||
            !!p.projectManager?.user?.userId;
          // Only show projects with no manager at all
          return !assigned;
        }
        return true;
      })
      .map((p: any) => ({ value: p.projectId as string, label: p.name }));
  }, [projects, isProjectManager]);

  return (
    <Modal
      title={title}
      cancelText="Cancel"
      isLoading={isLoading}
      confirmText={initialData ? 'Update' : 'Add User'}
      isOpen={!!showNewModal}
      onClose={toggleNewModal}
      handleConfirm={formik.handleSubmit}
      disableConfirm={isLoading}>
      <form className="flex flex-col gap-6">
        <FileDropZone
          files={avatarFiles}
          onFilesChange={(files) => setAvatarFiles(files.slice(-1))}
          multiple={false}
          maxFiles={1}
          acceptedTypes="images"
          avatar
          existingImageUrl={initialData?.user?.avatar}
          title="Drop avatar image here, or click to select"
          subtitle="PNG, JPG, GIF up to 50MB"
        />
        <div className="flex gap-6">
          <FormGroup label="Role" htmlFor="role">
            <ReactSelect
              placeholder="Select Role"
              options={roleOptions}
              // Resolve the displayed value against the FULL option list so a
              // current role this user may not assign still shows its label
              // instead of rendering blank (a blank select would submit an empty
              // role and demote the user).
              value={(() => {
                const currentRole =
                  formik.values.role || initialData?.user?.role || role || '';
                return (
                  USER_TYPE_OPTIONS.find((o) => o.value === currentRole) || null
                );
              })()}
              isDisabled={roleSelectLocked}
              onChange={(selected) => {
                const value = Array.isArray(selected)
                  ? null
                  : (selected as any)?.value || null;
                formik.setFieldValue('role', value || null);
              }}
            />
          </FormGroup>
        </div>
        <div className="flex gap-6">
          <FormGroup
            label="First Name"
            htmlFor="firstName"
            error={formik.errors.firstName}>
            <Input
              name="firstName"
              placeholder="First Name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.firstName}
            />
          </FormGroup>
          <FormGroup
            label="Last Name"
            htmlFor="lastName"
            error={formik.errors.lastName}>
            <Input
              name="lastName"
              placeholder="Last Name"
              onBlur={formik.handleBlur}
              value={formik.values.lastName}
              onChange={formik.handleChange}
            />
          </FormGroup>
        </div>
        <div className="flex gap-6">
          <FormGroup label="Email" htmlFor="email" error={formik.errors.email}>
            <Input
              name="email"
              placeholder="Email"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              onChange={formik.handleChange}
              disabled={!!initialData?.user?.userId}
            />
          </FormGroup>
        </div>

        {(isEmployee || isCrewLead) && (
          <>
            <div className="flex gap-6">
              <FormGroup label="DOB" htmlFor="dob" error={formik.errors.dob}>
                <Input
                  type="date"
                  name="dob"
                  placeholder="DOB"
                  value={formik.values.dob}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </FormGroup>
              <FormGroup
                label="Hire Date"
                htmlFor="hireDate"
                error={formik.errors.hireDate}>
                <Input
                  type="date"
                  name="hireDate"
                  placeholder="Hire Date"
                  onBlur={formik.handleBlur}
                  value={formik.values.hireDate}
                  onChange={formik.handleChange}
                />
              </FormGroup>
            </div>
            <div className="flex gap-6">
              <FormGroup
                label="Rate"
                htmlFor="rate"
                error={formik.errors.rate as string}>
                <InputCurrency
                  name="rate"
                  placeholder="Rate"
                  value={formik.values.rate}
                  onValueChange={(value) => {
                    formik.setFieldValue('rate', value);
                  }}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
              <FormGroup
                label="Pay Period"
                htmlFor="payPeriod"
                error={formik.errors.payPeriod}>
                <ReactSelect
                  name="payPeriod"
                  placeholder="Pay Period"
                  options={PAY_PERIOD_OPTIONS}
                  value={
                    formik.values.payPeriod
                      ? PAY_PERIOD_OPTIONS.find(
                          (o) => o.value === formik.values.payPeriod,
                        ) || undefined
                      : undefined
                  }
                  onChange={(selected) => {
                    formik.setFieldValue(
                      'payPeriod',
                      (selected as any)?.value || '',
                    );
                  }}
                />
              </FormGroup>
              <FormGroup label="Mode" htmlFor="mode" error={formik.errors.mode}>
                <ReactSelect
                  name="mode"
                  placeholder="Mode"
                  options={MODE_OPTIONS}
                  value={
                    formik.values.mode
                      ? {
                          value: formik.values.mode,
                          label:
                            MODE_OPTIONS.find(
                              (o) => o.value === formik.values.mode,
                            )?.label || '',
                        }
                      : undefined
                  }
                  onChange={(selected) => {
                    formik.setFieldValue(
                      'mode',
                      (selected as any)?.value || '',
                    );
                  }}
                />
              </FormGroup>
            </div>
          </>
        )}

        {(isProjectManager || isSuperintendent || isReviewer) && (
          <div className="flex gap-6">
            <FormGroup label="Projects" htmlFor="projects">
              <ReactSelect
                isMulti
                options={projectOptions}
                placeholder="Select projects"
                value={(isProjectManager
                  ? formik.values.managerProjectIds
                  : isSuperintendent
                    ? formik.values.superintendentProjectIds
                    : formik.values.reviewerProjectIds
                ).map(
                  (id) =>
                    projectOptions.find((o) => o.value === id) ||
                    (() => {
                      const p = (projects || []).find(
                        (pp: any) => pp.projectId === id,
                      );
                      return { value: id, label: p?.name || id };
                    })(),
                )}
                onChange={(selected) => {
                  const arr = Array.isArray(selected)
                    ? selected.map((s: any) => s.value as string)
                    : [];
                  if (isProjectManager) {
                    formik.setFieldValue('managerProjectIds', arr);
                  } else if (isSuperintendent) {
                    formik.setFieldValue('superintendentProjectIds', arr);
                  } else if (isReviewer) {
                    formik.setFieldValue('reviewerProjectIds', arr);
                  }
                }}
              />
            </FormGroup>
          </div>
        )}
      </form>
    </Modal>
  );
};
